// All five Game Report sub-tab panels live in a single file.
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Line, PieChart, Pie, Cell,
} from 'recharts';
import {
  getGameOverview, getGamePerformance, getBrandMargin,
  getGameTypeMix, getTopGames, getGamePlayers,
} from '../../api/client';
import { formatAmount, formatNumber } from '../../lib/format';
import { metricTip } from '../../lib/metric-info';
import { InfoTip } from '../InfoTip';
import { Th } from '../Th';
import { siteParam, siteName, useSites } from '../SiteFilter';
import { currencySymbol } from '../CurrencySelector';
import { ExportButtons } from '../ExportButtons';
import type { GameFilters } from './useGameFilters';

// ---------- shared utilities ----------

function useFetch<T>(fn: () => Promise<T>, deps: unknown[]): { data: T | null; loading: boolean; error: string | null } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fn()
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => { if (!cancelled) setError(String(err?.message || err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { data, loading, error };
}

function fmtMoney(symbol: string, v: number): string {
  return `${symbol}${formatAmount(v || 0)}`;
}

function fmtBucket(iso: string, granularity: string): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d);
  if (granularity === 'monthly') {
    return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'long' });
  }
  if (granularity === 'weekly') {
    const end = new Date(dt);
    end.setDate(end.getDate() + 6);
    const sameMonth = dt.getMonth() === end.getMonth();
    const left  = dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    const right = end.toLocaleDateString(undefined, sameMonth ? { day: 'numeric' } : { month: 'short', day: 'numeric' });
    return `${left} - ${right}`;
  }
  return iso;
}

// Compute delta percentage between current and previous values.
function deltaPct(current: number, prev: number): { pct: number; dir: 'up' | 'down' | 'flat' } {
  if (!prev || prev === 0) return { pct: 0, dir: 'flat' };
  const pct = ((current - prev) / Math.abs(prev)) * 100;
  return { pct, dir: pct > 0.5 ? 'up' : pct < -0.5 ? 'down' : 'flat' };
}

const SiteCell: React.FC<{ id: number | undefined }> = ({ id }) => {
  const { allSites } = useSites();
  if (id == null) return <>{''}</>;
  const name = siteName(allSites, id);
  return <span title={`ID ${id}`} className="cursor-help">{name}</span>;
};

// Hook: builds API params using useSites (must be called from a component)
function useApiParams(f: GameFilters) {
  const { allSites } = useSites();
  return {
    date_from: f.dateFrom,
    date_to: f.dateTo,
    site_ids: siteParam(allSites, f.siteIds),
    display_currency: f.currencyMode,
    period: f.granularity,
    game_type: f.gameType,
    brand_id: f.brandId,
  };
}

// ---------- Loading / Error ----------

const Loading: React.FC = () => (
  <div className="flex items-center justify-center py-12">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent border-t-transparent" />
  </div>
);

const ErrorMsg: React.FC<{ msg: string }> = ({ msg }) => (
  <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
    {msg}
  </div>
);

// ---------- KPI Card with delta ----------

const DeltaBadge: React.FC<{ current: number; prev: number; isPercent?: boolean }> = ({ current, prev, isPercent }) => {
  const { pct, dir } = deltaPct(current, prev);
  if (dir === 'flat' && prev === 0) return null;
  const color = dir === 'up' ? 'text-emerald-600 dark:text-emerald-400' : dir === 'down' ? 'text-red-500 dark:text-red-400' : 'text-slate-400';
  const arrow = dir === 'up' ? '\u2191' : dir === 'down' ? '\u2193' : '';
  const display = isPercent
    ? `${arrow} ${formatNumber(Math.abs(current * 100 - prev * 100), 1)}pp`
    : `${arrow} ${formatNumber(Math.abs(pct), 1)}%`;
  return <span className={`ml-2 text-xs font-medium ${color}`}>{display}</span>;
};

const KPICard: React.FC<{ label: string; value: string; current?: number; prev?: number; isPercent?: boolean }> = ({ label, value, current, prev, isPercent }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
    <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</p>
    <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
      {value}
      {current !== undefined && prev !== undefined && <DeltaBadge current={current} prev={prev} isPercent={isPercent} />}
    </p>
  </div>
);

// ---------- Sortable Table Header ----------

interface SortState { col: string; dir: 'asc' | 'desc' }

// Maps sortable column ids to metric-info keys for header tooltips.
const COL_INFO: Record<string, string> = {
  ggr: 'ggr', turnover: 'turnover', round_count: 'rounds', unique_players: 'uniquePlayers',
  effective_rtp: 'rtp', margin: 'margin', pct_of_total: 'pctOfTotal', total_bet: 'totalBet', total_win: 'totalWin',
};

const SortHeader: React.FC<{ label: string; col: string; sort: SortState; onSort: (col: string) => void }> = ({ label, col, sort, onSort }) => {
  const active = sort.col === col;
  const arrow = active ? (sort.dir === 'asc' ? ' \u25B2' : ' \u25BC') : '';
  const tip = metricTip(COL_INFO[col] || '');
  return (
    <th
      className="px-4 py-3 font-medium text-slate-700 dark:text-slate-300 cursor-pointer select-none hover:text-accent"
      onClick={() => onSort(col)}
    >
      <InfoTip tip={tip}>{label}</InfoTip>{arrow}
    </th>
  );
};

// ---------- Player Drill-Down Modal ----------

interface PlayerRow {
  player_id: string;
  site_id: number;
  currency: string;
  total_bet: number;
  total_win: number;
  ggr: number;
  round_count: number;
}

const PlayerDrillDown: React.FC<{
  gameId: number;
  gameName: string;
  filters: GameFilters;
  onClose: () => void;
}> = ({ gameId, gameName, filters, onClose }) => {
  const { t } = useTranslation();
  const p = useApiParams(filters);
  const sym = currencySymbol(filters.currencyMode);

  const { data, loading, error } = useFetch<{ data: PlayerRow[] }>(
    () => getGamePlayers({ ...p, game_id: gameId, limit: 50 }),
    [p.date_from, p.date_to, p.site_ids, p.display_currency, gameId],
  );

  const players = data?.data || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="mx-4 max-h-[80vh] w-full max-w-4xl overflow-auto rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              {t('gamePerformance.playerDrillDown', { defaultValue: 'Player Breakdown' })}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">{gameName} (ID: {gameId})</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800">
            &times;
          </button>
        </div>
        <div className="p-6">
          {loading && <Loading />}
          {error && <ErrorMsg msg={error} />}
          {!loading && !error && (
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800">
                <tr>
                  {[{ l: 'Player ID', i: 'player' }, { l: 'Site', i: 'site' }, { l: 'Currency', i: 'currency' }, { l: 'Total Bet', i: 'totalBet' }, { l: 'Total Win', i: 'totalWin' }, { l: 'GGR', i: 'ggr' }, { l: 'Rounds', i: 'rounds' }].map((h) => (
                    <Th key={h.l} tip={metricTip(h.i)}>{h.l}</Th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {players.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">{t('gamePerformance.noDataAvailable')}</td></tr>
                ) : players.map((row, i) => (
                  <tr key={i} className="bg-white dark:bg-slate-900">
                    <td className="px-4 py-3 font-mono text-xs">
                      <Link to={`/reports/players/detail?player_id=${encodeURIComponent(row.player_id)}&site_id=${row.site_id}`}
                        className="font-medium text-blue-600 underline decoration-dotted underline-offset-2 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                        {row.player_id}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-slate-900 dark:text-white"><SiteCell id={row.site_id} /></td>
                    <td className="px-4 py-3 text-slate-900 dark:text-white">{row.currency}</td>
                    <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtMoney(sym, row.total_bet)}</td>
                    <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtMoney(sym, row.total_win)}</td>
                    <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtMoney(sym, row.ggr)}</td>
                    <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber(row.round_count || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== OVERVIEW PANEL ====================

interface OverviewTotals {
  currency?: string;
  total_ggr: number;
  total_turnover: number;
  total_rounds: number;
  unique_players: number;
  unique_games: number;
  avg_rtp: number;
  prev_ggr: number;
  prev_turnover: number;
  prev_rounds: number;
  prev_players: number;
  prev_games: number;
  prev_rtp: number;
}

interface OverviewRow {
  date: string;
  ggr: number;
  turnover: number;
  round_count: number;
  unique_players: number;
  unique_games: number;
}

export const GameOverviewPanel: React.FC<{ filters: GameFilters }> = ({ filters }) => {
  const { t } = useTranslation();
  const p = useApiParams(filters);
  const sym = currencySymbol(filters.currencyMode);

  const { data, loading, error } = useFetch<{ data: { totals: OverviewTotals[]; rows: OverviewRow[] } }>(
    () => getGameOverview(p),
    [p.date_from, p.date_to, p.site_ids, p.display_currency, p.period, p.game_type, p.brand_id],
  );

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;
  if (!data?.data) return null;

  const totals = data.data.totals?.[0];
  const rows = data.data.rows || [];

  return (
    <div className="space-y-6">
      {/* KPI Cards with delta % */}
      {totals && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <KPICard label={t('gamePerformance.kpiTotalGgr', { defaultValue: 'Total GGR' })} value={fmtMoney(sym, totals.total_ggr)} current={totals.total_ggr} prev={totals.prev_ggr} />
          <KPICard label={t('gamePerformance.kpiTotalTurnover', { defaultValue: 'Total Turnover' })} value={fmtMoney(sym, totals.total_turnover)} current={totals.total_turnover} prev={totals.prev_turnover} />
          <KPICard label={t('gamePerformance.kpiTotalRounds', { defaultValue: 'Total Rounds' })} value={formatNumber(totals.total_rounds || 0)} current={totals.total_rounds} prev={totals.prev_rounds} />
          <KPICard label={t('gamePerformance.kpiUniquePlayers', { defaultValue: 'Unique Players' })} value={formatNumber(totals.unique_players || 0)} current={totals.unique_players} prev={totals.prev_players} />
          <KPICard label={t('gamePerformance.kpiUniqueGames', { defaultValue: 'Unique Games' })} value={formatNumber(totals.unique_games || 0)} current={totals.unique_games} prev={totals.prev_games} />
          <KPICard label={t('gamePerformance.kpiAvgRtp', { defaultValue: 'Avg RTP' })} value={`${formatNumber((totals.avg_rtp || 0) * 100, 2)}%`} current={totals.avg_rtp} prev={totals.prev_rtp} isPercent />
        </div>
      )}

      {/* Trend Chart */}
      <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">
          {t('gamePerformance.chartGgrTrend', { defaultValue: 'GGR Trend' })}
        </h2>
        <ResponsiveContainer width="100%" height={350}>
          <ComposedChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '11px' }}
              tickFormatter={(v: string) => fmtBucket(v, filters.granularity)} />
            <YAxis yAxisId="left" stroke="#9ca3af" tickFormatter={(v: number) => `${sym}${(v / 1000).toFixed(0)}k`} />
            <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" />
            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
              formatter={(v: number, name: string) => name === 'unique_games' ? formatNumber(v) : fmtMoney(sym, v)}
              labelFormatter={(v: string) => fmtBucket(v, filters.granularity)} />
            <Legend />
            <Bar yAxisId="left" dataKey="ggr" name="GGR" fill="#009EA0" radius={[4, 4, 0, 0]} />
            <Line yAxisId="right" dataKey="unique_games" name="Unique Games" stroke="#f59e0b" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Time-series Table */}
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              {[{ l: 'Date', i: 'date' }, { l: 'GGR', i: 'ggr' }, { l: 'Turnover', i: 'turnover' }, { l: 'Rounds', i: 'rounds' }, { l: 'Players', i: 'uniquePlayers' }, { l: 'Games', i: '' }].map((h) => (
                <Th key={h.l} tip={metricTip(h.i)}>{h.l}</Th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">{t('gamePerformance.noDataAvailable')}</td></tr>
            ) : rows.map((row, i) => (
              <tr key={i} className="bg-white dark:bg-slate-900">
                <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtBucket(row.date, filters.granularity)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtMoney(sym, row.ggr)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtMoney(sym, row.turnover)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber(row.round_count || 0)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber(row.unique_players || 0)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber(row.unique_games || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==================== GAME PERFORMANCE PANEL ====================

interface GameRow {
  date: string;
  site_id: number;
  brand_id: number;
  brand_name: string;
  game_id: number;
  game_name: string;
  game_type?: string;
  currency?: string;
  ggr: number;
  turnover: number;
  round_count: number;
  unique_players: number;
  effective_rtp: number;
}

export const GamePerformancePanel: React.FC<{ filters: GameFilters }> = ({ filters }) => {
  const { t } = useTranslation();
  const p = useApiParams(filters);
  const sym = currencySymbol(filters.currencyMode);

  const [sort, setSort] = useState<SortState>({ col: 'ggr', dir: 'desc' });
  const [drillDown, setDrillDown] = useState<{ gameId: number; gameName: string } | null>(null);

  const onSort = useCallback((col: string) => {
    setSort((prev) => prev.col === col ? { col, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { col, dir: 'desc' });
  }, []);

  const dimsParam = filters.dimensions.length > 0 ? filters.dimensions.join(',') : undefined;

  const { data, loading, error } = useFetch<{ data: GameRow[] }>(
    () => getGamePerformance({ ...p, sort_by: sort.col, sort_dir: sort.dir, limit: 100, dimensions: dimsParam }),
    [p.date_from, p.date_to, p.site_ids, p.display_currency, p.period, p.game_type, p.brand_id, sort.col, sort.dir, dimsParam],
  );

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;

  const rows = data?.data || [];

  // Determine the primary label key for the chart based on active dimensions.
  const dims = filters.dimensions;
  const chartLabelKey = dims.includes('game') ? 'game_name'
    : dims.includes('brand') ? 'brand_name'
    : dims.includes('game_type') ? 'game_type'
    : null;

  return (
    <div className="space-y-6">
      {/* Bar chart top 20 — only when there's a meaningful label dimension */}
      {chartLabelKey && rows.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">
            {dims.includes('game') ? t('gamePerformance.topGamesByGgr') : `GGR by ${chartLabelKey.replace('_name', '').replace('_', ' ')}`}
          </h2>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={rows.slice(0, 20)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis type="number" stroke="#9ca3af" tickFormatter={(v: number) => fmtMoney(sym, v)} />
              <YAxis dataKey={chartLabelKey} type="category" stroke="#9ca3af" width={180} style={{ fontSize: '11px' }}
                tickFormatter={(v: string) => v && v.length > 25 ? v.slice(0, 22) + '...' : (v || '')} />
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
                formatter={(v: number) => fmtMoney(sym, v)} />
              <Bar dataKey="ggr" name="GGR" fill="#009EA0" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Table with sortable headers */}
      {(() => {
        const d = filters.dimensions;
        const showDate = d.includes('date');
        const showSite = d.includes('site');
        const showBrand = d.includes('brand');
        const showGame = d.includes('game');
        const showGameType = d.includes('game_type');
        const showCurrency = d.includes('currency');
        const colCount = 5 + (showDate ? 1 : 0) + (showSite ? 1 : 0) + (showBrand ? 1 : 0) + (showGame ? 1 : 0) + (showGameType ? 1 : 0) + (showCurrency ? 1 : 0);
        const th = "px-4 py-3 font-medium text-slate-700 dark:text-slate-300";
        const td = "px-4 py-3 text-slate-900 dark:text-white";

        return (<>
      <div className="flex items-center justify-between">
        <ExportButtons reportType="game_performance" dateFrom={filters.dateFrom} dateTo={filters.dateTo} siteIds={p.site_ids} />
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              {showDate && <th className={th}><InfoTip tip={metricTip('date')}>{t('gamePerformance.columnDate')}</InfoTip></th>}
              {showSite && <th className={th}><InfoTip tip={metricTip('site')}>{t('gamePerformance.columnSite')}</InfoTip></th>}
              {showBrand && <th className={th}><InfoTip tip={metricTip('brand')}>{t('gamePerformance.columnBrand')}</InfoTip></th>}
              {showGame && <th className={th}><InfoTip tip={metricTip('game')}>{t('gamePerformance.columnGame')}</InfoTip></th>}
              {showGameType && <th className={th}><InfoTip tip={metricTip('gameType')}>{t('gamePerformance.columnGameType')}</InfoTip></th>}
              {showCurrency && <th className={th}><InfoTip tip={metricTip('currency')}>{t('gamePerformance.columnCurrency')}</InfoTip></th>}
              <SortHeader label="GGR" col="ggr" sort={sort} onSort={onSort} />
              <SortHeader label={t('gamePerformance.columnTurnover')} col="turnover" sort={sort} onSort={onSort} />
              <SortHeader label={t('gamePerformance.columnRounds')} col="round_count" sort={sort} onSort={onSort} />
              <SortHeader label={t('gamePerformance.columnUniquePlayers')} col="unique_players" sort={sort} onSort={onSort} />
              <SortHeader label={t('gamePerformance.columnRtp')} col="effective_rtp" sort={sort} onSort={onSort} />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {rows.length === 0 ? (
              <tr><td colSpan={colCount} className="px-4 py-8 text-center text-slate-500">{t('gamePerformance.noDataAvailable')}</td></tr>
            ) : rows.map((row, i) => (
              <tr key={i} className="bg-white hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800">
                {showDate && <td className={td}>{fmtBucket(row.date, filters.granularity)}</td>}
                {showSite && <td className={td}><SiteCell id={row.site_id} /></td>}
                {showBrand && <td className={td} title={`ID ${row.brand_id}`}>{row.brand_name || row.brand_id}</td>}
                {showGame && (
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setDrillDown({ gameId: row.game_id, gameName: row.game_name || String(row.game_id) })}
                      className="cursor-pointer font-medium text-blue-600 underline decoration-dotted underline-offset-2 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      title={`ID ${row.game_id} - Click for player breakdown`}
                    >
                      {row.game_name || row.game_id}
                    </button>
                  </td>
                )}
                {showGameType && <td className={td}>{row.game_type || ''}</td>}
                {showCurrency && <td className={td}>{row.currency || ''}</td>}
                <td className={td}>{fmtMoney(sym, row.ggr)}</td>
                <td className={td}>{fmtMoney(sym, row.turnover)}</td>
                <td className={td}>{formatNumber(row.round_count || 0)}</td>
                <td className={td}>{formatNumber(row.unique_players || 0)}</td>
                <td className={td}>{formatNumber((row.effective_rtp || 0) * 100, 2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        </>);
      })()}

      {/* Player drill-down modal */}
      {drillDown && (
        <PlayerDrillDown
          gameId={drillDown.gameId}
          gameName={drillDown.gameName}
          filters={filters}
          onClose={() => setDrillDown(null)}
        />
      )}
    </div>
  );
};

// ==================== BRAND MARGIN PANEL ====================

interface BrandRow {
  date: string;
  site_id: number;
  brand_id: number;
  brand_name: string;
  currency?: string;
  ggr: number;
  turnover: number;
  round_count: number;
  unique_players: number;
  effective_rtp: number;
  margin: number;
}

export const BrandMarginPanel: React.FC<{ filters: GameFilters }> = ({ filters }) => {
  const { t } = useTranslation();
  const p = useApiParams(filters);
  const sym = currencySymbol(filters.currencyMode);
  const isBase = filters.currencyMode === 'BASE';

  const [sort, setSort] = useState<SortState>({ col: 'ggr', dir: 'desc' });
  const onSort = useCallback((col: string) => {
    setSort((prev) => prev.col === col ? { col, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { col, dir: 'desc' });
  }, []);

  const { data, loading, error } = useFetch<{ data: BrandRow[] }>(
    () => getBrandMargin({ ...p, sort_by: sort.col, sort_dir: sort.dir, limit: 100 }),
    [p.date_from, p.date_to, p.site_ids, p.display_currency, p.period, p.game_type, p.brand_id, sort.col, sort.dir],
  );

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;

  const rows = data?.data || [];

  return (
    <div className="space-y-6">
      {/* Chart */}
      <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">{t('gamePerformance.brandRevenueMargin')}</h2>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={rows.slice(0, 20)}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="brand_name" stroke="#9ca3af" style={{ fontSize: '11px' }} angle={-25} textAnchor="end" height={60} />
            <YAxis stroke="#9ca3af" />
            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
              formatter={(v: number, name: string) => name === 'margin' ? `${formatNumber(v, 2)}%` : fmtMoney(sym, v)} />
            <Legend />
            <Bar dataKey="ggr" name="GGR" fill="#009EA0" radius={[4, 4, 0, 0]} />
            <Bar dataKey="margin" name="Margin %" fill="#f59e0b" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <div className="flex items-center justify-between">
        <ExportButtons reportType="brand_margin" dateFrom={filters.dateFrom} dateTo={filters.dateTo} siteIds={p.site_ids} />
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              <Th tip={metricTip('date')}>{t('gamePerformance.columnDate')}</Th>
              <Th tip={metricTip('site')}>{t('gamePerformance.columnSite')}</Th>
              <Th tip={metricTip('brand')}>{t('gamePerformance.columnBrand')}</Th>
              {isBase && <Th tip={metricTip('currency')}>{t('gamePerformance.columnCurrency')}</Th>}
              <SortHeader label="GGR" col="ggr" sort={sort} onSort={onSort} />
              <SortHeader label={t('gamePerformance.columnTurnover', { defaultValue: 'Turnover' })} col="turnover" sort={sort} onSort={onSort} />
              <SortHeader label={t('gamePerformance.columnMargin')} col="margin" sort={sort} onSort={onSort} />
              <SortHeader label={t('gamePerformance.columnRounds')} col="round_count" sort={sort} onSort={onSort} />
              <SortHeader label={t('gamePerformance.columnUniquePlayers')} col="unique_players" sort={sort} onSort={onSort} />
              <SortHeader label={t('gamePerformance.columnRtp')} col="effective_rtp" sort={sort} onSort={onSort} />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {rows.length === 0 ? (
              <tr><td colSpan={isBase ? 10 : 9} className="px-4 py-8 text-center text-slate-500">{t('gamePerformance.noDataAvailable')}</td></tr>
            ) : rows.map((row, i) => (
              <tr key={i} className="bg-white hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800">
                <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtBucket(row.date, filters.granularity)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white"><SiteCell id={row.site_id} /></td>
                <td className="px-4 py-3 text-slate-900 dark:text-white" title={`ID ${row.brand_id}`}>{row.brand_name || row.brand_id}</td>
                {isBase && <td className="px-4 py-3 text-slate-900 dark:text-white">{row.currency || ''}</td>}
                <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtMoney(sym, row.ggr)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtMoney(sym, row.turnover)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber(row.margin || 0, 2)}%</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber(row.round_count || 0)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber(row.unique_players || 0)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber((row.effective_rtp || 0) * 100, 2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ==================== GAME TYPE MIX PANEL ====================

const GAME_TYPE_COLORS: Record<string, string> = {
  casino:   '#009EA0',
  live:     '#f59e0b',
  crash:    '#ef4444',
  virtuals: '#8b5cf6',
};

interface MixRow {
  game_type: string;
  ggr: number;
  turnover: number;
  round_count: number;
  unique_players: number;
  pct_of_total: number;
}

export const GameTypeMixPanel: React.FC<{ filters: GameFilters }> = ({ filters }) => {
  const { t } = useTranslation();
  const p = useApiParams(filters);
  const sym = currencySymbol(filters.currencyMode);

  const { data, loading, error } = useFetch<{ data: MixRow[] }>(
    () => getGameTypeMix(p),
    [p.date_from, p.date_to, p.site_ids, p.display_currency, p.game_type, p.brand_id],
  );

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;

  const rows = data?.data || [];

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Donut Chart */}
        <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">
            {t('gamePerformance.chartGameTypeMix', { defaultValue: 'Turnover by Game Type' })}
          </h2>
          <ResponsiveContainer width="100%" height={350}>
            <PieChart>
              <Pie
                data={rows}
                dataKey="turnover"
                nameKey="game_type"
                cx="50%"
                cy="50%"
                innerRadius={80}
                outerRadius={130}
                label={({ game_type, pct_of_total }: MixRow) => `${game_type} ${formatNumber(pct_of_total, 1)}%`}
              >
                {rows.map((entry) => (
                  <Cell key={entry.game_type} fill={GAME_TYPE_COLORS[entry.game_type] || '#6b7280'} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
                formatter={(v: number) => fmtMoney(sym, v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Table */}
        <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">Breakdown</h2>
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                {[{ l: t('gamePerformance.columnGameType', { defaultValue: 'Game Type' }), i: 'gameType' }, { l: 'GGR', i: 'ggr' },
                  { l: t('gamePerformance.columnTurnover', { defaultValue: 'Turnover' }), i: 'turnover' },
                  { l: t('gamePerformance.columnRounds'), i: 'rounds' }, { l: t('gamePerformance.columnUniquePlayers'), i: 'uniquePlayers' },
                  { l: t('gamePerformance.columnPctOfTotal', { defaultValue: '% of Total' }), i: 'pctOfTotal' }].map((h) => (
                  <th key={h.l} className="px-3 py-2 font-medium text-slate-700 dark:text-slate-300"><InfoTip tip={metricTip(h.i)}>{h.l}</InfoTip></th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {rows.map((row) => (
                <tr key={row.game_type}>
                  <td className="px-3 py-2 text-slate-900 dark:text-white">
                    <span className="mr-2 inline-block h-3 w-3 rounded-full" style={{ backgroundColor: GAME_TYPE_COLORS[row.game_type] || '#6b7280' }} />
                    {row.game_type}
                  </td>
                  <td className="px-3 py-2 text-slate-900 dark:text-white">{fmtMoney(sym, row.ggr)}</td>
                  <td className="px-3 py-2 text-slate-900 dark:text-white">{fmtMoney(sym, row.turnover)}</td>
                  <td className="px-3 py-2 text-slate-900 dark:text-white">{formatNumber(row.round_count || 0)}</td>
                  <td className="px-3 py-2 text-slate-900 dark:text-white">{formatNumber(row.unique_players || 0)}</td>
                  <td className="px-3 py-2 text-slate-900 dark:text-white">{formatNumber(row.pct_of_total, 1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ==================== TOP GAMES PANEL ====================

export const TopGamesPanel: React.FC<{ filters: GameFilters }> = ({ filters }) => {
  const { t } = useTranslation();
  const p = useApiParams(filters);
  const sym = currencySymbol(filters.currencyMode);
  const [drillDown, setDrillDown] = useState<{ gameId: number; gameName: string } | null>(null);

  const { data, loading, error } = useFetch<{ data: GameRow[] }>(
    () => getTopGames({ ...p, top_n: filters.topN }),
    [p.date_from, p.date_to, p.site_ids, p.display_currency, p.game_type, p.brand_id, filters.topN],
  );

  if (loading) return <Loading />;
  if (error) return <ErrorMsg msg={error} />;

  const rows = data?.data || [];

  return (
    <div className="space-y-6">
      {/* Top N selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('gamePerformance.topNLabel', { defaultValue: 'Show top' })}
        </span>
        {[10, 20, 50].map((n) => (
          <button
            key={n}
            onClick={() => filters.setTopN(n)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              filters.topN === n
                ? 'bg-accent text-white'
                : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300'
            }`}
          >
            {n}
          </button>
        ))}
        <span className="text-sm text-slate-500 dark:text-slate-400">
          {t('gamePerformance.topNGames', { defaultValue: 'games' })}
        </span>
      </div>

      {/* Horizontal bar chart */}
      <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">
          {t('gamePerformance.chartTopGames', { defaultValue: 'Top Games by GGR' })}
        </h2>
        <ResponsiveContainer width="100%" height={Math.max(300, rows.length * 28)}>
          <BarChart data={rows} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis type="number" stroke="#9ca3af" tickFormatter={(v: number) => fmtMoney(sym, v)} />
            <YAxis dataKey="game_name" type="category" stroke="#9ca3af" width={200} style={{ fontSize: '11px' }}
              tickFormatter={(v: string) => v && v.length > 30 ? v.slice(0, 27) + '...' : (v || '')} />
            <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
              formatter={(v: number) => fmtMoney(sym, v)} />
            <Bar dataKey="ggr" name="GGR" fill="#009EA0" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Table ranking */}
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800">
            <tr>
              {[{ l: '#', i: '' }, { l: t('gamePerformance.columnGame'), i: 'game' }, { l: t('gamePerformance.columnBrand'), i: 'brand' },
                { l: t('gamePerformance.columnGameType', { defaultValue: 'Game Type' }), i: 'gameType' },
                { l: 'GGR', i: 'ggr' }, { l: t('gamePerformance.columnTurnover', { defaultValue: 'Turnover' }), i: 'turnover' },
                { l: t('gamePerformance.columnRounds'), i: 'rounds' }, { l: t('gamePerformance.columnUniquePlayers'), i: 'uniquePlayers' },
                { l: t('gamePerformance.columnRtp'), i: 'rtp' }].map((h) => (
                <Th key={h.l} tip={metricTip(h.i)}>{h.l}</Th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
            {rows.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">{t('gamePerformance.noDataAvailable')}</td></tr>
            ) : rows.map((row, i) => (
              <tr key={i} className="bg-white hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-800">
                <td className="px-4 py-3 font-bold text-accent">{i + 1}</td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => setDrillDown({ gameId: row.game_id, gameName: row.game_name || String(row.game_id) })}
                    className="cursor-pointer font-medium text-blue-600 underline decoration-dotted underline-offset-2 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    title={`ID ${row.game_id} - Click for player breakdown`}
                  >
                    {row.game_name || row.game_id}
                  </button>
                </td>
                <td className="px-4 py-3 text-slate-900 dark:text-white" title={`ID ${row.brand_id}`}>{row.brand_name || row.brand_id}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{row.game_type || ''}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtMoney(sym, row.ggr)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{fmtMoney(sym, row.turnover)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber(row.round_count || 0)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber(row.unique_players || 0)}</td>
                <td className="px-4 py-3 text-slate-900 dark:text-white">{formatNumber((row.effective_rtp || 0) * 100, 2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Player drill-down modal */}
      {drillDown && (
        <PlayerDrillDown
          gameId={drillDown.gameId}
          gameName={drillDown.gameName}
          filters={filters}
          onClose={() => setDrillDown(null)}
        />
      )}
    </div>
  );
};
